/* eslint-disable no-nested-ternary */
import {
  useFormAddProductsToCart,
  AddProductsToCartFields,
} from '@graphcommerce/magento-product/components'
import {
  ConfigurableOptionValue,
  UseConfigurableOptionsSelection,
  useConfigurableOptionsForSelection,
  ConfigurableOptionValueFragment,
} from '@graphcommerce/magento-product-configurable'
import { ConfigurableOptionsFragment } from '@graphcommerce/magento-product-configurable/graphql/ConfigurableOptions.gql'
import {
  nonNullable,
  filterNonNullableKeys,
  SectionHeader,
  ActionCardListForm,
  ActionCardItemBase,
  responsiveVal,
} from '@graphcommerce/next-ui'
import { useWatch } from '@graphcommerce/react-hook-form'
import { i18n } from '@lingui/core'
import { Box, SxProps, Theme } from '@mui/material'

type Props = NonNullable<
  NonNullable<ConfigurableOptionsFragment['configurable_options']>[number]
> & {
  index: number
  optionIndex: number
  optionEndLabels?: Record<string, React.ReactNode>
  sx?: SxProps<Theme>
  attribute_code: string
  render: typeof ConfigurableOptionValue
  unavailableOptions: string[]
} & UseConfigurableOptionsSelection

export function ConfigurableProductOption(props: Props) {
  const {
    values,
    label,
    index,
    optionIndex,
    optionEndLabels,
    sx,
    attribute_code,
    url_key,
    render,
    unavailableOptions,
    ...other
  } = props
  const fieldName = `cartItems.${index}.selected_options.${optionIndex}` as const

  const { control } = useFormAddProductsToCart()

  const selectedOption = useWatch({ control, name: fieldName })

  const selectedOptions = (useWatch({ control, name: `cartItems.${index}.selected_options` }) ?? [])
    .filter(nonNullable)
    .filter(Boolean)
    // This list is sorted by the the order in which the options are displayed.
    // By slicing were only taking the previous options in consideration and not the options below the current option.
    // .slice(0, optionIndex)
    .filter((o) => o !== selectedOption)

  const { configured } = useConfigurableOptionsForSelection({ url_key, selectedOptions })

  const available =
    configured?.configurable_product_options_selection?.options_available_for_selection?.find(
      (o) => o?.attribute_code === attribute_code,
    )?.option_value_uids

  const items = filterNonNullableKeys(values, ['uid']).map((ov) => ({
    value: ov.uid,
    ...ov,
    disabled: unavailableOptions.includes(ov.uid)
      ? true
      : !(!available || available.includes(ov.uid)),
  }))

  const isColorSwatch = attribute_code.includes('color') || attribute_code.includes('colour')

  if (!values) return null

  return (
    <Box
      id={`filter-${attribute_code}`}
      key={fieldName}
      sx={[
        isColorSwatch && {
          '& .layoutGrid': {
            gridTemplateColumns: `repeat(auto-fill, minmax(${responsiveVal(35, 45)}, 1fr))!important`,
            gap: '5px !important',
          },
          '& .MuiButtonBase-root.ActionCard-root': {
            padding: 0,
            width: responsiveVal(35, 45),
            height: responsiveVal(35, 45),
            boxShadow: 'none',
            borderRadius: '50%',
          },
          '& .layoutGrid > div': {
            justifyContent: 'center',
            borderRadius: '50%',
            overflow: 'hidden',
            boxShadow: '0px 1px 4px 0px rgba(0,0,0,0.125)',
            display: 'flex',
            width: responsiveVal(35, 45),
            height: responsiveVal(35, 45),
          },
          '& .ActionCard-image': {
            paddingRight: 'unset',
            '& ~ *': { display: 'none' },
            '& img': {
              padding: 0,
            },
            '&.selected img': {
              border: '2px solid #CB8792',
              padding: '2px',
            },
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      className={isColorSwatch ? 'color-swatch' : ''}
    >
      <SectionHeader
        labelLeft={label}
        labelRight={optionEndLabels?.[attribute_code ?? '']}
        sx={{ mt: 0 }}
      />

      <ActionCardListForm<
        ActionCardItemBase & ConfigurableOptionValueFragment,
        AddProductsToCartFields
      >
        layout='grid'
        {...other}
        name={fieldName}
        control={control}
        required
        items={items}
        render={render}
        rules={{
          required: i18n._(/* i18n*/ 'Please select a value for ‘{label}’', { label }),
        }}
      />
    </Box>
  )
}
